import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzResultModule } from 'ng-zorro-antd/result';
import { map } from 'rxjs';
import { TenantContextService } from 'shared/core';
import { unitTypeLabel } from '../tenancy/unit-type-style';

/**
 * Where a vertical's route gate sends a tenant of another unit type.
 *
 * The gate is `canMatch: [matchUnitType(x)]` on the vertical's mount, and a
 * failed canMatch falls through to the same-path fallback, which redirects
 * here with the area in the URL (`denied/tannery`). Says which unit type the
 * area is for and which the company is; the way out is another company.
 */
@Component({
  selector: 'kf-denied',
  imports: [NzResultModule, NzButtonModule],
  template: `
    <nz-result nzStatus="403" nzTitle="Not available here" [nzSubTitle]="message()">
      <div nz-result-extra>
        <button nz-button nzType="default" (click)="switchTenant()">Choose another company</button>
      </div>
    </nz-result>
  `,
})
export class DeniedComponent {
  private readonly context = inject(TenantContextService);
  private readonly router = inject(Router);
  private readonly area = toSignal(inject(ActivatedRoute).paramMap.pipe(map((params) => params.get('area'))), { initialValue: null });

  readonly message = computed(() => {
    const area = this.area();
    const tenant = this.context.selectedTenant();
    const forWhom = area ? `This area is for ${unitTypeLabel(area)} units.` : 'This area is for another unit type.';
    const whoYouAre = tenant ? `${tenant.CompanyName} is a ${unitTypeLabel(tenant.UnitType)} unit.` : 'No company is selected.';
    return `${forWhom} ${whoYouAre}`;
  });

  switchTenant(): void {
    this.router.navigate(['/tenant-select']);
  }
}
